import React, { memo, useCallback, useMemo } from 'react';
import { Typography, Box, Grid, IconButton, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { toLocaleStringIn } from '../../util/general';
import Chart from '../../assets/icons/Chart';
import { handleOverviewSecurityClick } from '../securityInfo/SecurityOverviewHeader';
import ExitIcon from '../../assets/icons/ExitIcon';
import { Holding } from '../../reducer/holdings.reducer.ts';
import { getSecurityKey } from '../../util/security';
import { selectPositionLtpsSignature } from '../../selectors/security';
import { parseLtpsSignature } from '../../util/positionLtpsSignature';
import { RootState } from '../../store';

interface HoldingsRowProps {
    holding: Holding;
    onSquareOff?: (holding: Holding) => void;
}

const HoldingsRow: React.FC<HoldingsRowProps> = ({ holding, onSquareOff }) => {
    const { security, quantity, averageBuyPrice: rawAverageBuyPrice, t1Quantity } = holding;
    const multiplier = security?.priceMultiplier ?? 100;
    const averageBuyPrice = rawAverageBuyPrice / multiplier;
    const investedAmount = (quantity + t1Quantity) * averageBuyPrice;
    const securityCode = security?.code;
    const securityKey = useMemo(
        () => (security ? getSecurityKey(security.exchange, security.code) : ''),
        [security]
    );
    const ltpsSignature = useSelector(selectPositionLtpsSignature(securityKey ? [securityKey] : []));
    const ltp = useMemo(() => {
        const rawLtp = parseLtpsSignature(ltpsSignature)[0] ?? 0;
        return rawLtp / multiplier;
    }, [ltpsSignature, multiplier]);
    const previousClose = useSelector((state: RootState) => {
        if (!securityKey) return 0;
        return (state.securityStore.snapshots[securityKey]?.previousClose ?? 0) / multiplier;
    });
    const pnl = (ltp - averageBuyPrice) * (quantity + t1Quantity);
    const dayPnlPercentage = previousClose > 0 ? ((ltp - previousClose) / previousClose) * 100 : null;

    const handleChartClick = useCallback(() => {
        if (security) {
            handleOverviewSecurityClick(security);
        }
    }, [security]);

    const handleSquareOffClick = useCallback(() => {
        if (security && onSquareOff) {
            onSquareOff(holding);
        }
    }, [security, onSquareOff, holding]);

    return (
        <Box
            sx={{
                padding: '12px',
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '&:hover .action-buttons': {
                    visibility: 'visible'
                }
            }}
        >
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="textSecondary">
                            Qty. {quantity}
                        </Typography>
                        {!(t1Quantity == 0) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    backgroundColor: (theme) => theme.palette.mode === 'light' ? '#e3f2fd' : 'rgba(144, 202, 249, 0.16)',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                }}
                            >
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '11px' }}>
                                    T1: {t1Quantity}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="caption" color="textSecondary">
                            • Avg. {toLocaleStringIn(averageBuyPrice)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="subtitle2">{securityCode}</Typography>
                        <Box
                            className="action-buttons"
                            sx={{
                                display: 'flex',
                                gap: 0.25,
                                visibility: 'hidden'
                            }}
                        >
                            <Tooltip
                                title="View Chart"
                                arrow
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            borderRadius: '6px'
                                        }
                                    }
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleChartClick}
                                    sx={{
                                        padding: '2px',
                                        color: '#1976d2',
                                        borderRadius: '4px',
                                        '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                            color: '#1565c0'
                                        }
                                    }}
                                >
                                    <Chart fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title="Exit"
                                arrow
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            borderRadius: '6px'
                                        }
                                    }
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleSquareOffClick}
                                    sx={{
                                        padding: '2px',
                                        color: '#1976d2',
                                        borderRadius: '4px',
                                        '& .MuiSvgIcon-root': { fontSize: '16px' },
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                            color: '#1565c0'
                                        }
                                    }}
                                >
                                    <ExitIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                        Invested: {toLocaleStringIn(investedAmount)}
                    </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" className={pnl >= 0 ? 'green-text' : 'red-text'}>
                        {investedAmount > 0 ? (
                            `${pnl >= 0 ? '+' : ''}${((pnl / investedAmount) * 100).toFixed(2)}%`
                        ) : (
                            '--'
                        )}
                    </Typography>
                    <Typography variant="subtitle2" className={pnl >= 0 ? 'green-text' : 'red-text'}>
                        {pnl >= 0 ? '+' : ''}
                        {toLocaleStringIn(pnl)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        LTP: {toLocaleStringIn(ltp)}
                        <span style={{ fontSize: '12px', marginLeft: '4px' }} className={dayPnlPercentage !== null && dayPnlPercentage >= 0 ? 'green-text' : 'red-text'}>
                            {dayPnlPercentage !== null ? (
                                `${dayPnlPercentage >= 0 ? '(+' : '('}${dayPnlPercentage.toFixed(2)}%)`
                            ) : (
                                '(--)'
                            )}
                        </span>
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    )
}

export default memo(
    HoldingsRow,
    (prev, next) => prev.holding === next.holding && prev.onSquareOff === next.onSquareOff
);
